import { create } from "zustand";
import { isAxiosError } from "axios";
import {
  fetchScoringProfiles,
  createScoringProfile,
  updateScoringProfile,
  deleteScoringProfile,
} from "@/apis/niffler/scoringProfiles";
import {
  fetchScoringCatalog,
  type ScoringModel,
} from "@/apis/argo/scoringModels";
import type { ScoringProfile, ScoringProfileCreate } from "@/lib/scoring/types";

function extractMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    return (err.response?.data as { message?: string })?.message ?? err.message;
  }
  return err instanceof Error ? err.message : fallback;
}

export const MAX_ACTIVE_PROFILES = 3;

// ── Unified scoring source ids ─────────────────────────────────────────────
// Legacy (niffler) profiles and new (argo) models are selected from one unified
// list, capped by a single shared budget. Each active entry is a namespaced
// string id so the two id spaces (numeric profile id, string model family)
// coexist: `v1:<profileId>` / `v2:<family>`.

export const V1_PREFIX = "v1:";
export const V2_PREFIX = "v2:";
export const v1SourceId = (profileId: number) => `${V1_PREFIX}${profileId}`;
export const v2SourceId = (family: string) => `${V2_PREFIX}${family}`;

export type ParsedSourceId =
  | { kind: "v1"; profileId: number }
  | { kind: "v2"; family: string }
  | null;

export function parseSourceId(sid: string): ParsedSourceId {
  if (sid.startsWith(V1_PREFIX)) {
    const n = Number(sid.slice(V1_PREFIX.length));
    return Number.isNaN(n) ? null : { kind: "v1", profileId: n };
  }
  if (sid.startsWith(V2_PREFIX)) {
    return { kind: "v2", family: sid.slice(V2_PREFIX.length) };
  }
  return null;
}

/** Splits the unified active-source list into the legacy/new mirror arrays
 * (order preserved) that downstream consumers still read directly. */
function deriveMirrors(activeSourceIds: string[]): {
  activeProfileIds: number[];
  activeModelFamilies: string[];
} {
  const activeProfileIds: number[] = [];
  const activeModelFamilies: string[] = [];
  for (const sid of activeSourceIds) {
    const parsed = parseSourceId(sid);
    if (parsed?.kind === "v1") activeProfileIds.push(parsed.profileId);
    else if (parsed?.kind === "v2") activeModelFamilies.push(parsed.family);
  }
  return { activeProfileIds, activeModelFamilies };
}

/**
 * Keeps the matrix "focused" and "compare" selections valid as the set of active
 * profiles changes: focus defaults to the first active profile, compare to the
 * first active profile that isn't the focused one. Matrix is V1-only, so this
 * operates on the legacy profile subset of the active sources.
 */
function reconcileSelection(
  activeProfileIds: number[],
  focusedProfileId: number | null,
  compareProfileId: number | null
): { focusedProfileId: number | null; compareProfileId: number | null } {
  const focused =
    focusedProfileId != null && activeProfileIds.includes(focusedProfileId)
      ? focusedProfileId
      : (activeProfileIds[0] ?? null);
  const compare =
    compareProfileId != null &&
    compareProfileId !== focused &&
    activeProfileIds.includes(compareProfileId)
      ? compareProfileId
      : (activeProfileIds.find((id) => id !== focused) ?? null);
  return { focusedProfileId: focused, compareProfileId: compare };
}

interface ScoringState {
  profiles: ScoringProfile[];
  /** Canonical unified selection — namespaced source ids, one shared budget. */
  activeSourceIds: string[];
  /** Derived mirror: numeric ids of active legacy profiles (selection order). */
  activeProfileIds: number[];
  /** Profile the Matrix view is focused on (V1 only). */
  focusedProfileId: number | null;
  /** Second profile used by the Compare view (vs the focused one). */
  compareProfileId: number | null;
  isLoading: boolean;
  error: string | null;
  fetchProfiles: (email: string) => Promise<void>;
  activateProfile: (id: number) => void;
  deactivateProfile: (id: number) => void;
  setActiveProfileIds: (ids: number[]) => void;
  setFocusedProfile: (id: number | null) => void;
  setCompareProfileId: (id: number | null) => void;
  createProfile: (payload: ScoringProfileCreate) => Promise<ScoringProfile>;
  updateProfile: (profile: ScoringProfile) => Promise<void>;
  deleteProfile: (id: number) => Promise<void>;

  // ── V2 (argo model) data + selection ──────────────────────────────────────
  models: ScoringModel[];
  /** Derived mirror: families of active models (selection order). */
  activeModelFamilies: string[];
  isModelsLoading: boolean;
  modelsError: string | null;
  fetchModels: (tenantId: string) => Promise<void>;
  activateModel: (family: string) => void;
  deactivateModel: (family: string) => void;
  setActiveModelFamilies: (families: string[]) => void;

  // ── Unified selection API (used by the merged picker) ─────────────────────
  activateSource: (sourceId: string) => void;
  deactivateSource: (sourceId: string) => void;
  /** Replaces the whole unified selection at once (e.g. hydrating from a URL). */
  setActiveSourceIds: (ids: string[]) => void;
}

export const useScoringStore = create<ScoringState>((set, get) => {
  /** Applies a new unified active-source list: refreshes the mirror arrays and
   * reconciles the matrix focus/compare against the resulting V1 subset. */
  const applyActiveSources = (
    activeSourceIds: string[],
    state: ScoringState
  ) => {
    const mirrors = deriveMirrors(activeSourceIds);
    return {
      activeSourceIds,
      ...mirrors,
      ...reconcileSelection(
        mirrors.activeProfileIds,
        state.focusedProfileId,
        state.compareProfileId
      ),
    };
  };

  return {
    profiles: [],
    activeSourceIds: [],
    activeProfileIds: [],
    focusedProfileId: null,
    compareProfileId: null,
    isLoading: false,
    error: null,

    fetchProfiles: async (email) => {
      set({ isLoading: true, error: null });
      try {
        const profiles = await fetchScoringProfiles(email);
        const fetchedIds = new Set(profiles.map((p) => p.id));
        set((state) => {
          // Drop active v1 sources whose profile no longer exists.
          const activeSourceIds = state.activeSourceIds.filter((sid) => {
            const parsed = parseSourceId(sid);
            return parsed?.kind === "v1"
              ? fetchedIds.has(parsed.profileId)
              : true;
          });
          return {
            profiles,
            isLoading: false,
            ...applyActiveSources(activeSourceIds, state),
          };
        });
      } catch (err) {
        set({
          error: extractMessage(err, "Failed to load scoring profiles"),
          isLoading: false,
        });
      }
    },

    activateProfile: (id) => get().activateSource(v1SourceId(id)),
    deactivateProfile: (id) => get().deactivateSource(v1SourceId(id)),

    setActiveProfileIds: (ids) => {
      set((state) => {
        // Replace the v1 selection, preserving any active v2 models.
        const v2Sources = state.activeSourceIds.filter(
          (sid) => parseSourceId(sid)?.kind === "v2"
        );
        const next = [...new Set(ids)]
          .map(v1SourceId)
          .concat(v2Sources)
          .slice(0, MAX_ACTIVE_PROFILES);
        return applyActiveSources(next, state);
      });
    },

    setFocusedProfile: (id) => {
      set((state) => ({
        ...reconcileSelection(
          state.activeProfileIds,
          id,
          state.compareProfileId
        ),
      }));
    },

    setCompareProfileId: (id) => {
      set((state) => ({
        ...reconcileSelection(
          state.activeProfileIds,
          state.focusedProfileId,
          id
        ),
      }));
    },

    createProfile: async (payload) => {
      try {
        const created = await createScoringProfile(payload);
        set((state) => ({ profiles: [...state.profiles, created] }));
        return created;
      } catch (err) {
        throw new Error(
          extractMessage(err, "Failed to create scoring profile")
        );
      }
    },

    updateProfile: async (profile) => {
      try {
        const updated = await updateScoringProfile(profile);
        set((state) => ({
          profiles: state.profiles.map((p) =>
            p.id === updated.id ? updated : p
          ),
        }));
      } catch (err) {
        throw new Error(
          extractMessage(err, "Failed to update scoring profile")
        );
      }
    },

    deleteProfile: async (id) => {
      try {
        await deleteScoringProfile(id);
        set((state) => {
          const activeSourceIds = state.activeSourceIds.filter(
            (sid) => sid !== v1SourceId(id)
          );
          return {
            profiles: state.profiles.filter((p) => p.id !== id),
            ...applyActiveSources(activeSourceIds, state),
          };
        });
      } catch (err) {
        throw new Error(
          extractMessage(err, "Failed to delete scoring profile")
        );
      }
    },

    // ── V2 (argo model) data + selection ────────────────────────────────────
    models: [],
    activeModelFamilies: [],
    isModelsLoading: false,
    modelsError: null,

    fetchModels: async (tenantId) => {
      set({ isModelsLoading: true, modelsError: null });
      try {
        const models = await fetchScoringCatalog(tenantId);
        const fetchedFamilies = new Set(models.map((m) => m.family));
        set((state) => {
          // Drop active v2 sources no longer in the catalog.
          const activeSourceIds = state.activeSourceIds.filter((sid) => {
            const parsed = parseSourceId(sid);
            return parsed?.kind === "v2"
              ? fetchedFamilies.has(parsed.family)
              : true;
          });
          return {
            models,
            isModelsLoading: false,
            ...applyActiveSources(activeSourceIds, state),
          };
        });
      } catch (err) {
        set({
          modelsError: extractMessage(err, "Failed to load scoring models"),
          isModelsLoading: false,
        });
      }
    },

    activateModel: (family) => get().activateSource(v2SourceId(family)),
    deactivateModel: (family) => get().deactivateSource(v2SourceId(family)),

    setActiveModelFamilies: (families) => {
      set((state) => {
        // Replace the v2 selection, preserving any active v1 profiles.
        const v1Sources = state.activeSourceIds.filter(
          (sid) => parseSourceId(sid)?.kind === "v1"
        );
        const next = v1Sources
          .concat([...new Set(families)].map(v2SourceId))
          .slice(0, MAX_ACTIVE_PROFILES);
        return applyActiveSources(next, state);
      });
    },

    // ── Unified selection API ─────────────────────────────────────────────
    activateSource: (sourceId) => {
      set((state) => {
        if (
          state.activeSourceIds.includes(sourceId) ||
          state.activeSourceIds.length >= MAX_ACTIVE_PROFILES
        )
          return {};
        return applyActiveSources([...state.activeSourceIds, sourceId], state);
      });
    },

    deactivateSource: (sourceId) => {
      set((state) =>
        applyActiveSources(
          state.activeSourceIds.filter((sid) => sid !== sourceId),
          state
        )
      );
    },

    setActiveSourceIds: (ids) => {
      set((state) => {
        // Keep only well-formed, unique source ids, capped by the shared budget.
        const next = [...new Set(ids)]
          .filter((sid) => parseSourceId(sid) !== null)
          .slice(0, MAX_ACTIVE_PROFILES);
        return applyActiveSources(next, state);
      });
    },
  };
});
